import { useEffect, useState } from "react";
import { useNotify, useDataProvider, useGetList } from "react-admin";
import { CardContent, Card, Button, Grid, Typography, Divider } from "@mui/material";
import { Cancel as CancelIcon } from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import SiteBulkForm from "./SiteBulkForm";
import { lvService } from "../../../services/LvService";
import { DataGrid } from "@mui/x-data-grid";

dayjs.extend(utc);

const SiteBulkEdit = ({ selectedSiteIds, onCancel, onError }) => {
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const { data, isLoading, isError, isSuccess, error, refetch } = useGetList("portal/sites");

    const [availableToUserStatus, setAvailableToUserStatus] = useState(false);
    const [customerVisibilityDttm, setCustomerVisibilityDttm] = useState(null);
    const [selectedSites, setSelectedSites] = useState([]);

    useEffect(() => {
        if (error) {
            console.error(error);
            onError();
        }
    }, [error]);

    useEffect(() => {
        if (!isLoading && isSuccess && !isError) {
            const records = data.filter((item) => selectedSiteIds.includes(item.id));
            setSelectedSites(records);

            // Determine if all selected sites have the same available_to_user status
            // If they do, set the bulk edit switch to that value, otherwise set it to false (indicating mixed values)
            const dttmValues = records.map((r) => r.customer_visibility_dttm ?? null);
            const allSame = dttmValues.every((v) => v === dttmValues[0]);
            setCustomerVisibilityDttm(allSame && dttmValues[0] ? dayjs.utc(dttmValues[0]) : null);
        }
    }, [data, isLoading, isSuccess]);

    const onSaveAvailableToUser = async () => {
        notify("Updating...");
        const updatePromises = selectedSiteIds.map(async (id) =>
            dataProvider.update("portal/sites", {
                id: id,
                data: { available_to_user: availableToUserStatus, id: id },
            })
        );

        await Promise.all(updatePromises)
            .then(() => {
                refetch();
                notify("Elements updated");
            })
            .catch(() => {
                console.error(error);
                notify("Elements failed to update", { type: "error" });
            });
    };

    const onSaveCustomerVisibilityDttm = async () => {
        notify("Updating...");
        const uniqueSiteGroupIds = [...new Set(selectedSites.map((s) => s.site_group_id))];
        const dttmValue = customerVisibilityDttm ? customerVisibilityDttm.utc().format() : null;
        const updatePromises = uniqueSiteGroupIds.map((id) =>
            lvService.updateSiteGroupCustomerVisibilityDttm(id, dttmValue)
        );

        await Promise.all(updatePromises)
            .then(() => {
                refetch();
                notify("Elements updated");
            })
            .catch(() => {
                console.error(error);
                notify("Elements failed to update", { type: "error" });
            });
    };

    return (
        <>
            <div style={{ marginTop: "16px", marginLeft: "16px", marginRight: "16px", width: "auto" }}>
                <Grid container justifyContent="flex-start" spacing={3}>
                    <Grid item xs={7} mr={3} ml={3}>
                        <Card>
                            <CardContent
                                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                            >
                                <Typography variant="h6">Bulk Edit Selected Sites</Typography>
                                <Button variant="contained" startIcon={<CancelIcon />} onClick={onCancel}>
                                    Cancel
                                </Button>
                            </CardContent>
                            <Divider />

                            <div
                                style={{
                                    display: "flex",
                                    width: "auto",
                                    height: "75vh",
                                    overflowY: "auto",
                                    padding: "8px 32px",
                                    alignItems: "center",
                                }}
                            >
                                <DataGrid
                                    rows={selectedSites}
                                    columns={[
                                        { field: "id", headerName: "ID", flex: 1 },
                                        { field: "site_group_name", headerName: "Site Group Name", flex: 2 },
                                        { field: "lineId", headerName: "Line ID", flex: 1 },
                                        { field: "lineName", headerName: "Line Name", flex: 1 },
                                        {
                                            field: "available_to_user",
                                            headerName: "Available To User",
                                            flex: 1,
                                            renderCell: (params) => (params.value ? "Yes" : "No"),
                                        },
                                        {
                                            field: "customer_visibility_dttm",
                                            headerName: "Customer Visibility",
                                            flex: 2,
                                            renderCell: (params) =>
                                                params.value
                                                    ? dayjs.utc(params.value).format("YYYY-MM-DD hh:mm A UTC")
                                                    : "",
                                        },
                                    ]}
                                    disableSelectionOnClick
                                />
                            </div>
                        </Card>
                    </Grid>
                    <Grid item xs={4} mr={1}>
                        <SiteBulkForm
                            onError={onError}
                            availableToUserStatus={availableToUserStatus}
                            setAvailableToUserStatus={setAvailableToUserStatus}
                            onSaveAvailableToUser={onSaveAvailableToUser}
                            customerVisibilityDttm={customerVisibilityDttm}
                            setCustomerVisibilityDttm={setCustomerVisibilityDttm}
                            onSaveCustomerVisibilityDttm={onSaveCustomerVisibilityDttm}
                        />
                    </Grid>
                </Grid>
            </div>
        </>
    );
};

export default SiteBulkEdit;
